import { draftFromMessage } from './descriptionDraft';
import { LOCATION_DESCRIPTION_COLUMN, parseValueForField, resolveField } from './editableFields';
import { buildExamples } from './exampleCopy';
import type { AssistantIntent } from './types';

/**
 * Deterministic natural-language intent parser, scoped to the single location
 * the chat was opened from.
 *
 * This is a rule-based stand-in for a model-backed planner and sits behind the
 * same `text -> AssistantIntent` contract a backend endpoint would expose, so it
 * can be replaced without touching the conversation state machine or the UI.
 *
 * It is deliberately conservative: anything it cannot confidently classify
 * returns UNKNOWN rather than guessing at a mutation.
 */

const APPEND_VERBS = /\b(add|append|include|insert)\b/;
const SET_VERBS =
  /\b(set|change|update|make|adjust|bump|raise|lower|turn|switch|toggle|flip|enable|disable|stop|start)\b/;

/**
 * Word-boundary "and", used to find candidate split points for a message
 * naming more than one field ("set capacity to 20010 **and** turn on
 * overages"). A location name can itself contain "and" ("Parking and
 * Storage"), so every occurrence is a *candidate* split, tried rightmost
 * first — see {@link splitCompoundFields}.
 */
const AND_WORD = /\band\b/gi;

const QUOTE_PATTERNS: RegExp[] = [
  /"([^"]+)"/,
  /'([^']+)'/,
  /\u201c([^\u201d]+)\u201d/,
  /\u2018([^\u2019]+)\u2019/,
];

const extractQuotedText = (input: string): string | null => {
  for (const pattern of QUOTE_PATTERNS) {
    const match = input.match(pattern);
    if (match && match[1].trim()) return match[1].trim();
  }
  return null;
};

/**
 * Generic fallback help. The parser has no account context, so it cannot name
 * the user's own locations here — {@link buildExamples} does that, and the
 * conversation layer swaps this out via the `genericHelp` flag. This text must
 * therefore stay free of invented location names, since it is what an account
 * with no usable location names actually sees.
 */
const UNKNOWN_HELP = buildExamples().unknownHelp;

/** UNKNOWN carrying the generic help, flagged so callers can personalise it. */
const genericUnknown = (): AssistantIntent => ({
  action: 'UNKNOWN',
  reason: UNKNOWN_HELP,
  genericHelp: true,
});

/**
 * "Spaces" on its own maps to three different columns, so it is not treated as a
 * synonym for any of them. Naming them back to the user is far more useful than
 * the generic help text, and safer than picking one.
 */
const AMBIGUOUS_SPACES =
  'There are a few space counts I can set \u2014 which did you mean: the total number of ' +
  'spaces (capacity), the minimum number of spaces, or auto-approve spaces?';

const MENTIONS_SPACES = /\bspaces?\b/;

/**
 * Tries to resolve a whole clause to a plain field-set, exactly like the tail
 * of {@link parseIntent} but returning `null` instead of an `UNKNOWN` intent
 * on any miss. Descriptions are deliberately excluded, since a drafted or
 * literal description is a single intent by definition and often contains its
 * own "and".
 */
const parseFieldClause = (clause: string): { column: string; label: string; value: string } | null => {
  const field = resolveField(clause);
  if (!field || field.column === LOCATION_DESCRIPTION_COLUMN) return null;

  const lower = clause.toLowerCase();
  if (!SET_VERBS.test(lower) && !APPEND_VERBS.test(lower)) return null;

  const parsed = parseValueForField(field, clause);
  if (parsed.error || parsed.value === null) return null;

  return { column: field.column, label: field.label, value: parsed.value };
};

/**
 * Tries to read a message as two field-sets joined by "and" — "set capacity
 * to 20010 and turn on overages" — without assuming every "and" in the
 * message is a clause separator, since a location name can contain one too
 * ("Haystacks best **Parking and Storage**").
 *
 * Every occurrence of the word "and" is a candidate split point, tried
 * rightmost first: the two sides only count as a compound instruction if
 * *both* independently resolve to their own field-set on their own. Trying
 * right-to-left means the first candidate that works is the one that leaves
 * the most text on the left — exactly what you want when the two real
 * instructions are near the end ("...capacity to 20010 and turn on
 * overages"), since an earlier "and" inside a location name fails to parse as
 * its own clause and is skipped over.
 *
 * Only ever splits into two clauses; a message with three or more
 * instructions falls back to being parsed as one (and likely only the first
 * is recognised), which is a smaller gap than mis-splitting a location name.
 */
const splitCompoundFields = (
  text: string,
): { column: string; label: string; value: string }[] | null => {
  const splitPoints: number[] = [];
  const matcher = new RegExp(AND_WORD);
  let match: RegExpExecArray | null;
  while ((match = matcher.exec(text)) !== null) {
    splitPoints.push(match.index);
  }

  for (let i = splitPoints.length - 1; i >= 0; i -= 1) {
    const index = splitPoints[i];
    const before = parseFieldClause(text.slice(0, index).trim());
    if (!before) continue;
    const after = parseFieldClause(text.slice(index + 3).trim());
    if (!after) continue;

    // Later clause wins if the same field is named twice.
    const byColumn = new Map([before, after].map((field) => [field.column, field]));
    return Array.from(byColumn.values());
  }

  return null;
};

/**
 * Parses a user message into an actionable intent.
 *
 * Recognised forms:
 *  - `set the capacity to 1433` -> SET_FIELD on the matched column
 *  - `set the capacity to 1433 and turn on overages` -> SET_FIELDS, one per
 *    "and"-joined clause
 *  - `add "Open 24/7" to the description` -> APPEND_TO_DESCRIPTION (literal)
 *  - `update the description to make sure they ask for Bobby` -> DRAFT_DESCRIPTION
 */
export const parseIntent = (input: string): AssistantIntent => {
  const trimmed = input.trim();
  if (!trimmed) return genericUnknown();

  const fields = splitCompoundFields(trimmed);
  if (fields) {
    return fields.length === 1 ? { action: 'SET_FIELD', ...fields[0] } : { action: 'SET_FIELDS', fields };
  }

  const lower = trimmed.toLowerCase();
  const field = resolveField(trimmed);

  if (!field) {
    // The ambiguous-spaces reply is specific and already names the options, so
    // it is not flagged for substitution.
    return MENTIONS_SPACES.test(lower)
      ? { action: 'UNKNOWN', reason: AMBIGUOUS_SPACES }
      : genericUnknown();
  }

  if (field.column === LOCATION_DESCRIPTION_COLUMN) {
    // A quoted string is an explicit, literal instruction and always wins over
    // drafting — the user has already given us the exact words.
    const quoted = extractQuotedText(trimmed);
    if (quoted) {
      return { action: 'APPEND_TO_DESCRIPTION', text: quoted };
    }

    const draft = draftFromMessage(trimmed);
    if (draft) {
      return { action: 'DRAFT_DESCRIPTION', draft };
    }

    return {
      action: 'UNKNOWN',
      reason:
        'Tell me what the description should say \u2014 for example "update the description to ' +
        'make sure they ask for Bobby at the gate", or put exact wording in quotes.',
    };
  }

  if (!SET_VERBS.test(lower) && !APPEND_VERBS.test(lower)) {
    return genericUnknown();
  }

  const parsed = parseValueForField(field, trimmed);
  if (parsed.error || parsed.value === null) {
    return parsed.error ? { action: 'UNKNOWN', reason: parsed.error } : genericUnknown();
  }

  return { action: 'SET_FIELD', column: field.column, label: field.label, value: parsed.value };
};
